/**
 * [BLURY] | 🥭 useFittedFrame
 * Fits the preview frame inside the stage without distorting the output ratio.
 * Done in JS rather than with CSS aspect-ratio so both dimensions stay
 * constrained by the available space.
 * @build 13.09.26
 * @updated 13.09.26 PHT
 * @author TONYTONY Sàrl
 */
import { useEffect, useMemo, useState, type RefObject } from 'react';
import type { Size } from '@/engine/types';

const STAGE_GUTTER = 96;

export function useFittedFrame(stageRef: RefObject<HTMLElement | null>, size: Size): Size {
  const [stage, setStage] = useState<Size>({ width: 0, height: 0 });

  useEffect(() => {
    const el = stageRef.current;
    if (!el) {
      console.warn('useFittedFrame: no stage element found, skipping');
      return;
    }
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setStage({ width, height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [stageRef]);

  return useMemo(() => {
    const availableW = stage.width - STAGE_GUTTER;
    const availableH = stage.height - STAGE_GUTTER;
    if (availableW <= 0 || availableH <= 0) return { width: 0, height: 0 };
    const k = Math.min(availableW / size.width, availableH / size.height);
    return { width: Math.round(size.width * k), height: Math.round(size.height * k) };
  }, [stage, size]);
}
